/*
 * Matrix Digital Rain — runs on every .matrix-rain-canvas element.
 *
 * Adapted from the version running in production on trocp.jmckalex.org
 * (originally injected via Ghost's code injection feature).
 *
 * Key features:
 * - Multi-canvas: every .matrix-rain-canvas gets its own independent instance
 * - Per-canvas configuration: canvases with the .matrix-rain-canvas--nav
 *   modifier get smaller, slower rain tuned to the nav strip's proportions
 * - HiDPI/retina support via devicePixelRatio
 * - Sized to its parent container, not the window
 * - ResizeObserver-based: handles container resize from ANY cause
 *   (window resize, devtools opening, CSS changes, etc.)
 * - Debounced resize: coalesces rapid resize events via requestAnimationFrame
 * - Resize-safe: preserves column state across viewport changes
 * - Tan-on-black palette tuned for the TROCP design
 */
(function() {
    // Default config — used for the hero canvas (tall, prominent rain)
    const DEFAULT_CONFIG = {
        // Typography
        fontSize:      14,
        fontFamily:    "'JetBrains Mono', Menlo, monospace",

        // Animation
        speed:         55,        // ms between frames (lower = faster)
        dropSpeed:     0.88,      // probability a column advances each frame
        respawnChance: 0.975,     // probability a finished column restarts
        glyphFlicker:  0.02,      // chance a glyph mutates each frame
        fadeAlpha:     0.05,      // trail length (lower = longer trails)

        // Colours — tuned for TROCP palette
        brightColor:   '#b8a060', // leading glyph
        glyphColor:    '#8b7355', // main glyph (matches --accent)
        dimColor:      '#3a2f20', // darker trail glyph (currently unused, kept for reference)
    };

    // Nav-specific overrides — applied on top of DEFAULT_CONFIG when the
    // canvas has class .matrix-rain-canvas--nav.
    //
    // The nav strip is short (~60px), so we tune for proportion rather than
    // absolute size:
    //   - smaller glyphs so they don't feel oversized relative to the strip
    //   - slower drop speed so columns don't respawn frantically
    //   - shorter trail so glyphs don't leave a wake longer than the strip
    const NAV_CONFIG_OVERRIDES = {
        fontSize:    10,    // smaller relative to nav height
        dropSpeed:   0.35,  // slower advance (was 0.88)
        speed:       70,    // slightly slower frame rate
        fadeAlpha:   0.08,  // trail fades faster (shorter trails)
    };

    // Build the shared glyph set once: katakana + digits + symbols
    let glyphs = '';
    for (let i = 0xFF65; i <= 0xFF9F; i++) glyphs += String.fromCharCode(i);
    glyphs += '0123456789><+=$@#%&|{}[]~^';

    function randomGlyph() {
        return glyphs[Math.floor(Math.random() * glyphs.length)];
    }

    /*
     * Initialise a single canvas instance with its own config.
     * Each call returns an independent rain animation with its own state.
     */
    function initRain(canvas, config) {
        const container = canvas.parentElement;
        const ctx = canvas.getContext('2d');

        // Per-canvas state — closed over by this instance's resize/draw
        let W, H, columns = 0, drops = [], glyphGrid = [];
        let lastWidth = 0, lastHeight = 0;

        function resize() {
            const rect = container.getBoundingClientRect();
            if (rect.width === 0 || rect.height === 0) return;

            if (rect.width === lastWidth && rect.height === lastHeight) return;
            lastWidth = rect.width;
            lastHeight = rect.height;

            const dpr = window.devicePixelRatio || 1;

            W = canvas.width = Math.round(rect.width * dpr);
            H = canvas.height = Math.round(rect.height * dpr);
            canvas.style.width = rect.width + 'px';
            canvas.style.height = rect.height + 'px';

            const colCount = Math.ceil(rect.width / config.fontSize);
            const rows = Math.ceil(rect.height / config.fontSize);

            const oldDrops = drops.slice();
            const oldGrid = glyphGrid.slice();
            drops = [];
            glyphGrid = [];

            for (let i = 0; i < colCount; i++) {
                drops[i] = (i < oldDrops.length) ? oldDrops[i] : Math.random() * -rows;
                glyphGrid[i] = (i < oldGrid.length) ? oldGrid[i] : [];
                while (glyphGrid[i].length < rows + 20) {
                    glyphGrid[i].push(randomGlyph());
                }
            }
            columns = colCount;

            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.fillStyle = '#000';
            ctx.fillRect(0, 0, W, H);
        }

        function draw() {
            const rect = container.getBoundingClientRect();
            const realW = rect.width;
            const realH = rect.height;
            if (realW === 0 || realH === 0) return;

            const dpr = window.devicePixelRatio || 1;

            ctx.setTransform(1, 0, 0, 1, 0, 0);
            ctx.scale(dpr, dpr);

            ctx.fillStyle = 'rgba(0, 0, 0, ' + config.fadeAlpha + ')';
            ctx.fillRect(0, 0, realW, realH);

            ctx.font = config.fontSize + 'px ' + config.fontFamily;

            for (let i = 0; i < columns; i++) {
                const y = Math.floor(drops[i]);

                if (config.glyphFlicker > 0) {
                    for (let r = Math.max(0, y - 20); r <= y && r < glyphGrid[i].length; r++) {
                        if (Math.random() < config.glyphFlicker) {
                            glyphGrid[i][r] = randomGlyph();
                        }
                    }
                }

                if (y >= 0 && y < glyphGrid[i].length) {
                    ctx.fillStyle = config.brightColor;
                    ctx.globalAlpha = 1;
                    ctx.fillText(glyphGrid[i][y], i * config.fontSize, y * config.fontSize);
                }

                const trailLen = 3;
                for (let t = 1; t <= trailLen; t++) {
                    const ty = y - t;
                    if (ty >= 0 && ty < glyphGrid[i].length) {
                        const alpha = 1 - (t / (trailLen + 1));
                        ctx.fillStyle = config.glyphColor;
                        ctx.globalAlpha = alpha;
                        ctx.fillText(glyphGrid[i][ty], i * config.fontSize, ty * config.fontSize);
                        ctx.globalAlpha = 1;
                    }
                }

                if (Math.random() < config.dropSpeed) {
                    drops[i]++;
                    const newY = Math.floor(drops[i]);
                    if (newY >= 0) {
                        while (glyphGrid[i].length <= newY) glyphGrid[i].push(randomGlyph());
                        glyphGrid[i][newY] = randomGlyph();
                    }
                }

                if (drops[i] * config.fontSize > realH) {
                    if (Math.random() < config.respawnChance) {
                        drops[i] = Math.random() * -20;
                    }
                }
            }
        }

        // Coalesce rapid resize events to one call per animation frame.
        let resizeScheduled = false;
        function scheduleResize() {
            if (resizeScheduled) return;
            resizeScheduled = true;
            requestAnimationFrame(() => {
                resize();
                resizeScheduled = false;
            });
        }

        // Boot this instance
        resize();
        setInterval(draw, config.speed);

        if (typeof ResizeObserver !== 'undefined') {
            const ro = new ResizeObserver(scheduleResize);
            ro.observe(container);
        } else {
            window.addEventListener('resize', scheduleResize);
        }
    }

    // Find every matrix-rain canvas on the page and initialise it with the
    // appropriate config. Nav canvases get smaller/slower rain; everything
    // else gets the default (hero-tuned) config.
    const canvases = document.querySelectorAll('.matrix-rain-canvas');
    canvases.forEach(function(canvas) {
        const isNav = canvas.classList.contains('matrix-rain-canvas--nav');
        const config = isNav
            ? Object.assign({}, DEFAULT_CONFIG, NAV_CONFIG_OVERRIDES)
            : DEFAULT_CONFIG;
        initRain(canvas, config);
    });
})();
